import { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import type { ImportCertificateParams } from '@/hooks/data/useCertificates';
import type { Domain } from '@/types/domain';
import type { Node } from '@/types/node';

export interface ImportCertificateModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Domains that can receive an imported certificate */
  domains: Domain[];
  /** Nodes whose type supports certificate import */
  nodes: Node[];
  onImport: (params: ImportCertificateParams) => Promise<unknown>;
}

/**
 * Import an existing certificate from a Plesk node.
 * Mount with a `key` (or only while open) to reset the form.
 */
export function ImportCertificateModal({
  isOpen,
  onClose,
  domains,
  nodes,
  onImport,
}: ImportCertificateModalProps) {
  const [domainId, setDomainId] = useState('');
  const [nodeId, setNodeId] = useState('');
  const [pleskDomain, setPleskDomain] = useState('');

  const importAction = useAsyncAction(onImport, { errorMessage: 'Failed to import certificate' });
  const isImporting = importAction.isPending();

  const handleImport = async () => {
    if (!domainId || !nodeId) return;
    const result = await importAction.run({
      domainId,
      nodeId,
      pleskDomain: pleskDomain.trim() || undefined,
    });
    if (result.ok) onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalHeader>Import Certificate from Plesk</ModalHeader>
      <ModalBody>
        <p className="certs-modal-description">
          Import an existing SSL certificate from a Plesk node. The certificate
          will be fetched from the Plesk server and stored in the control plane.
        </p>

        <div className="certs-modal-field">
          <label htmlFor="import-domain-select" className="certs-modal-label">
            Domain *
          </label>
          <Select
            id="import-domain-select"
            value={domainId}
            onChange={setDomainId}
            placeholder="Select a domain..."
            options={domains.map((domain) => ({ value: domain.id, label: domain.name }))}
            className="certs-modal-select"
            required
          />
        </div>

        <div className="certs-modal-field">
          <label htmlFor="import-node-select" className="certs-modal-label">
            Plesk Node *
          </label>
          <Select
            id="import-node-select"
            value={nodeId}
            onChange={setNodeId}
            placeholder="Select a Plesk node..."
            options={nodes.map((node) => ({
              value: node.id,
              label: `${node.name} (${node.plesk_host ?? node.hostname})`,
            }))}
            className="certs-modal-select"
            required
          />
        </div>

        <div className="certs-modal-field">
          <label htmlFor="import-plesk-domain" className="certs-modal-label">
            Plesk Subscription
          </label>
          <input
            id="import-plesk-domain"
            type="text"
            value={pleskDomain}
            onChange={(e) => setPleskDomain(e.target.value)}
            className="certs-search-input"
            placeholder="e.g. dgtl.tech (if domain belongs to another subscription)"
          />
          <p className="certs-modal-hint">
            Leave empty if domain name matches the Plesk site name. Fill in the parent subscription
            name if the domain is hosted under a different Plesk subscription.
          </p>
        </div>

        {importAction.error && (
          <div className="modal-error" role="alert">
            {importAction.error}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={isImporting}>
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          isLoading={isImporting}
          disabled={!domainId || !nodeId || isImporting}
        >
          Import Certificate
        </Button>
      </ModalFooter>
    </Modal>
  );
}
